import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import type { InProductionOrder, OrderWorkflowProgress, WorkflowProgressItem } from '../services/api';

const CATEGORY_ORDER = ['parts_pull', 'cutting', 'track', 'fabric', 'assembly', 'quality', 'packing', 'shipping', 'other'];

const CATEGORY_LABELS: Record<string, string> = {
  parts_pull: 'Parts Pull',
  cutting: 'Cutting',
  track: 'Track',
  fabric: 'Fabric',
  assembly: 'Assembly',
  quality: 'Quality',
  packing: 'Packing',
  shipping: 'Shipping',
  other: 'Other',
};

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'complete':
      return (
        <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      );
    case 'in_progress':
      return <span className="w-3 h-3 rounded-full bg-blue-400 animate-pulse flex-shrink-0 mx-1" />;
    case 'ready':
      return <span className="w-3 h-3 rounded-full border-2 border-yellow-400 flex-shrink-0 mx-1" />;
    case 'blocked':
      return (
        <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      );
    case 'skipped':
      return <span className="w-5 h-5 text-gray-500 flex-shrink-0 text-center font-bold leading-5">&mdash;</span>;
    default: // pending
      return <span className="w-3 h-3 rounded-full border-2 border-gray-600 flex-shrink-0 mx-1" />;
  }
}

// Will Call vs Shipping (freight). Orders now run work-card-only, so the floor
// can't tell from the steps whether a finished order gets picked up (will call)
// or goes to the shipping building for freight — this badge says it plainly.
function shipKind(method?: string): 'willcall' | 'ship' | 'unknown' {
  const v = (method || '').toLowerCase().replace(/[\s_-]/g, '');
  if (v === 'willcall') return 'willcall';
  if (v === 'shipping' || v === 'ship' || v === 'freight') return 'ship';
  return 'unknown';
}

function ShippingBadge({ method }: { method?: string }) {
  const kind = shipKind(method);
  const cfg = {
    willcall: { label: 'WILL CALL', cls: 'bg-purple-500/15 text-purple-300 border-purple-500/40' },
    ship: { label: 'SHIPPING', cls: 'bg-sky-500/15 text-sky-300 border-sky-500/40' },
    unknown: { label: 'METHOD NOT SET', cls: 'bg-gray-600/20 text-gray-400 border-gray-600' },
  }[kind];
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-md border text-xs font-bold tracking-wide ${cfg.cls}`}>
      {kind === 'willcall' && (
        <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 7h18M3 7l1.5 12a1 1 0 001 1h11a1 1 0 001-1L19 7M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2" />
        </svg>
      )}
      {kind === 'ship' && (
        <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM20 17a2 2 0 11-4 0 2 2 0 014 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16V6a1 1 0 00-1-1H3v11m10-5h5l3 4v1h-3M13 16H9m11 1h-1" />
        </svg>
      )}
      {cfg.label}
    </span>
  );
}

function statusLabel(status: string): string {
  switch (status) {
    case 'complete': return 'Complete';
    case 'in_progress': return 'In Progress';
    case 'ready': return 'Ready';
    case 'blocked': return 'Blocked';
    case 'skipped': return 'Skipped';
    default: return 'Pending';
  }
}

function statusColor(status: string): string {
  switch (status) {
    case 'complete': return 'text-green-500';
    case 'in_progress': return 'text-blue-400';
    case 'ready': return 'text-yellow-400';
    case 'blocked': return 'text-red-500';
    case 'skipped': return 'text-gray-500';
    default: return 'text-gray-600';
  }
}

// Group steps by category maintaining order
function groupByCategory(steps: WorkflowProgressItem[]): [string, WorkflowProgressItem[]][] {
  const groups: Record<string, WorkflowProgressItem[]> = {};
  for (const step of steps) {
    const cat = step.category || 'other';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(step);
  }
  return CATEGORY_ORDER.filter(c => groups[c]).map(c => [c, groups[c]]);
}

// ============ Order List View ============

function OrderList({
  orders,
  search,
  onSearchChange,
  onSelect,
  onViewDetails,
  loading,
  onRefresh,
}: {
  orders: InProductionOrder[];
  search: string;
  onSearchChange: (v: string) => void;
  onSelect: (id: string) => void;
  onViewDetails: (orderId: string) => void;
  loading: boolean;
  onRefresh: () => void;
}) {
  const filtered = orders.filter(
    (o) =>
      o.orderNumber.toLowerCase().includes(search.toLowerCase()) ||
      o.companyName.toLowerCase().includes(search.toLowerCase()) ||
      (o.sidemark && o.sidemark.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Orders in Production</h1>
        <button
          onClick={onRefresh}
          className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by order #, company, or sidemark..."
          className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-yellow-400"
        />
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Order Cards */}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          {orders.length === 0 ? 'No orders in production' : 'No orders match your search'}
        </div>
      )}

      {!loading &&
        filtered.map((order) => (
          <div
            key={order.id}
            className="w-full bg-gray-800 rounded-xl p-4 text-left hover:bg-gray-750 transition-colors border border-gray-700"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-yellow-400 font-mono font-bold text-lg">{order.orderNumber}</span>
              <span className="text-sm text-gray-400">{order.totalItems} unit{order.totalItems !== 1 ? 's' : ''}</span>
            </div>
            <div className="text-white font-medium">{order.companyName}</div>
            {order.sidemark && <div className="text-gray-400 text-sm">{order.sidemark}</div>}

            {/* Will Call vs Shipping — so the floor knows how this order finishes */}
            <div className="mt-2">
              <ShippingBadge method={order.shippingMethod} />
            </div>

            {/* Progress Bar */}
            <div className="mt-3">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-gray-400">Progress</span>
                <span className="text-white font-medium">{order.progress}%</span>
              </div>
              <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${order.progress}%`,
                    background: order.progress === 100
                      ? '#22c55e'
                      : 'linear-gradient(90deg, #3b82f6, #22c55e)',
                  }}
                />
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500 mt-1">
                <span>{order.completedWorkOrders}/{order.totalWorkOrders} steps</span>
                {order.inProgressWorkOrders > 0 && (
                  <span className="text-blue-400">{order.inProgressWorkOrders} in progress</span>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => onViewDetails(order.id)}
                className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 active:bg-gray-500 text-white text-sm font-medium rounded-lg transition-colors border border-gray-600"
              >
                View Details
              </button>
              <button
                onClick={() => onSelect(order.id)}
                className="flex-1 px-4 py-2.5 bg-yellow-400/10 hover:bg-yellow-400/20 active:bg-yellow-400/30 text-yellow-400 text-sm font-medium rounded-lg transition-colors border border-yellow-400/30"
              >
                View Progress
              </button>
            </div>
          </div>
        ))}
    </div>
  );
}

// ============ Order Progress Detail View ============

function OrderProgressDetail({
  progress,
  loading,
  onBack,
  onStepClick,
  onRestart,
  onCompleteStep,
  restarting,
  completing,
}: {
  progress: OrderWorkflowProgress | null;
  loading: boolean;
  onBack: () => void;
  onStepClick: (step: WorkflowProgressItem, action: 'start' | 'view') => void;
  onRestart: (step: WorkflowProgressItem) => void;
  onCompleteStep: (step: WorkflowProgressItem) => void;
  restarting: string | null;
  completing: string | null;
}) {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!progress) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>Failed to load progress</p>
        <button onClick={onBack} className="mt-4 px-4 py-2 bg-gray-700 text-white rounded-lg">
          Back
        </button>
      </div>
    );
  }

  const grouped = groupByCategory(progress.steps);
  const { completed, total, percentage } = progress.overallProgress;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-700"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-white">{progress.orderNumber}</h1>
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <span>{(progress as any).companyName}</span>
            {(progress as any).sidemark && <span>— {(progress as any).sidemark}</span>}
          </div>
        </div>
      </div>

      {/* Overall Progress */}
      <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <span className="text-gray-400 text-sm">Overall Progress</span>
          <span className="text-white font-bold text-lg">{percentage}%</span>
        </div>
        <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${percentage}%`,
              background: percentage === 100
                ? '#22c55e'
                : 'linear-gradient(90deg, #3b82f6, #22c55e)',
            }}
          />
        </div>
        <div className="text-sm text-gray-500 mt-1">{completed}/{total} steps complete</div>
      </div>

      {/* Workflow Steps by Category */}
      {grouped.map(([category, steps]) => (
        <div key={category} className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
          {/* Category Header */}
          <div className="px-4 py-3 bg-gray-750 border-b border-gray-700">
            <h3 className="text-sm font-bold text-gray-300 uppercase tracking-wider">
              {CATEGORY_LABELS[category] || category}
            </h3>
          </div>

          {/* Steps */}
          <div className="divide-y divide-gray-700">
            {steps.map((step) => {
              const hasLink = !!(step.workOrderId || step.category === 'parts_pull');
              const canComplete = step.workOrderId && (step.status === 'ready' || step.status === 'in_progress');
              const isCompleting = completing === step.id;
              return (
                <div
                  key={step.id}
                  className="px-4 py-3 min-h-[56px]"
                >
                  <div className="flex items-center gap-3">
                    <StatusIcon status={step.status} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-sm font-medium truncate">
                          {step.stepDisplayName}
                        </span>
                        {step.orderItemId && step.itemNumber && (
                          <span className="text-xs text-gray-500">Unit {step.itemNumber}</span>
                        )}
                      </div>
                      {step.blockedReason && (
                        <p className="text-xs text-red-400 mt-0.5 truncate">{step.blockedReason}</p>
                      )}
                      {step.completedByName && step.completedAt && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {step.completedByName} &middot; {new Date(step.completedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </div>
                    <span className={`text-xs font-medium flex-shrink-0 ${statusColor(step.status)}`}>
                      {statusLabel(step.status)}
                    </span>
                  </div>

                  {/* Action buttons row */}
                  {hasLink && (
                    <div className="flex items-center gap-2 mt-2 ml-8">
                      {canComplete && (
                        <button
                          onClick={() => onStepClick(step, 'start')}
                          className="px-3 py-1.5 text-xs font-bold rounded-md transition-colors bg-yellow-400 hover:bg-yellow-300 active:bg-yellow-500 text-gray-900"
                        >
                          Open Work Card
                        </button>
                      )}
                      {step.workOrderId && (step.status === 'complete' || step.status === 'skipped') && (
                        <button
                          onClick={() => onRestart(step)}
                          disabled={restarting === step.id}
                          className="px-2 py-1.5 text-xs font-medium rounded-md transition-colors bg-orange-600 hover:bg-orange-500 active:bg-orange-700 text-white disabled:opacity-50"
                        >
                          {restarting === step.id ? '...' : 'Restart'}
                        </button>
                      )}
                      <button
                        onClick={() => onStepClick(step, 'view')}
                        className="px-2 py-1.5 text-xs rounded-md transition-colors bg-gray-700 hover:bg-gray-600 active:bg-gray-500 text-gray-300"
                      >
                        View
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ Main Page ============

export function OrdersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Restricted operators go order → station list → work card, never through
  // the work-order detail page (cut sheet / BOM).
  const workCardsOnly = (user as any)?.permissions?.work_cards_only === true;
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState<InProductionOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');

  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [progress, setProgress] = useState<OrderWorkflowProgress | null>(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const [restartingStepId, setRestartingStepId] = useState<string | null>(null);
  const [completingStepId, setCompletingStepId] = useState<string | null>(null);

  useEffect(() => {
    loadOrders();
  }, []);

  useEffect(() => {
    if (selectedOrderId) {
      loadProgress(selectedOrderId);
    }
  }, [selectedOrderId]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const data = await api.getOrdersInProduction();
      setOrders(data);
    } catch (err) {
      console.error('Failed to load orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadProgress = async (orderId: string) => {
    setProgressLoading(true);
    setProgress(null);
    try {
      const data = await api.getOrderWorkflowProgress(orderId);
      setProgress(data);
    } catch (err) {
      console.error('Failed to load progress:', err);
    } finally {
      setProgressLoading(false);
    }
  };

  const handleStepClick = async (step: WorkflowProgressItem, action: 'start' | 'view') => {
    // Work-cards-only employees never open the work-order detail page — a
    // "view" tap opens the work card for that station instead.
    if (action === 'view' && workCardsOnly && step.workstationId) {
      action = 'start';
    }
    if (action === 'start') {
      // Actually START the work order before opening the card, so started_at
      // reflects when work began. Previously this only navigated, leaving
      // started_at NULL until completion stamped both timestamps together —
      // which is why every station recorded ~0.03s of labor. Only fires for
      // 'ready' work orders; anything already in progress just opens.
      if (step.workOrderId && step.status === 'ready') {
        try {
          await api.startWorkOrder(step.workOrderId);
        } catch (err: any) {
          console.error('Failed to start work order:', err);
          alert(err?.response?.data?.message || 'Could not start this work order');
          return;
        }
      }
      // Navigate to work card page with workstation + specific work order
      if (step.workstationId) {
        const params = new URLSearchParams({ ws: step.workstationId });
        if (step.workOrderId) params.set('wo', step.workOrderId);
        navigate(`/work-cards?${params.toString()}`);
      } else {
        navigate('/work-cards');
      }
    } else if (step.category === 'parts_pull') {
      navigate(`/parts-pull/${step.orderId}`);
    } else if (step.workOrderId) {
      navigate(`/work/${step.workOrderId}`);
    }
  };

  // "View Details" — navigate to the work card for the first active WO
  const handleViewDetails = async (orderId: string) => {
    try {
      const data = await api.getOrderWorkflowProgress(orderId);
      // Work-cards-only employees stop at the station list: tap the order, see
      // its workstations, tap a station to open that work card. They never pass
      // through the work-order detail page.
      if (workCardsOnly) {
        setSelectedOrderId(orderId);
        setProgress(data);
        return;
      }
      const steps = data.steps || [];
      // Find best step to navigate to: prefer in_progress, then ready, then first with WO
      const inProg = steps.find(s => s.status === 'in_progress' && s.workOrderId);
      const ready = steps.find(s => s.status === 'ready' && s.workOrderId);
      const anyWo = steps.find(s => s.workOrderId);
      const target = inProg || ready || anyWo;
      if (target?.workOrderId) {
        navigate(`/work/${target.workOrderId}`);
      } else {
        // Fallback: show progress view
        setSelectedOrderId(orderId);
        setProgress(data);
      }
    } catch (err) {
      console.error('Failed to load order details:', err);
      // Fallback to progress view
      setSelectedOrderId(orderId);
    }
  };

  const handleRestartStep = async (step: WorkflowProgressItem) => {
    if (!step.workOrderId) return;
    setRestartingStepId(step.id);
    try {
      await api.post('/production/workstation/restart-step', {
        work_order_id: step.workOrderId,
      });
      if (selectedOrderId) {
        await loadProgress(selectedOrderId);
      }
    } catch (err) {
      console.error('Failed to restart step:', err);
    } finally {
      setRestartingStepId(null);
    }
  };

  /** Complete a work order directly from the progress page.
   *  If the step is 'ready', start it first, then complete it. */
  const handleCompleteStep = async (step: WorkflowProgressItem) => {
    if (!step.workOrderId) return;
    setCompletingStepId(step.id);
    try {
      // If still in 'ready' status, start it first
      if (step.status === 'ready') {
        await api.startWorkOrder(step.workOrderId);
      }
      // Now complete it
      await api.completeWorkOrder(step.workOrderId);

      // Reload progress to reflect new state
      if (selectedOrderId) {
        await loadProgress(selectedOrderId);
        // Also refresh the orders list so the progress bar updates
        loadOrders();
      }
    } catch (err: any) {
      console.error('Failed to complete step:', err);
      alert(err?.message || 'Failed to complete step');
      // Reload anyway to get fresh state
      if (selectedOrderId) await loadProgress(selectedOrderId);
    } finally {
      setCompletingStepId(null);
    }
  };

  if (selectedOrderId) {
    return (
      <OrderProgressDetail
        progress={progress}
        loading={progressLoading}
        onBack={() => {
          setSelectedOrderId(null);
          setProgress(null);
        }}
        onStepClick={handleStepClick}
        onRestart={handleRestartStep}
        onCompleteStep={handleCompleteStep}
        restarting={restartingStepId}
        completing={completingStepId}
      />
    );
  }

  return (
    <OrderList
      orders={orders}
      search={search}
      onSearchChange={setSearch}
      onSelect={setSelectedOrderId}
      onViewDetails={handleViewDetails}
      loading={loading}
      onRefresh={loadOrders}
    />
  );
}
